import { getMapChromeTheme } from "@/components/mapChromeTheme";
import {
  DECORATION_SET_DYNAMIC_DESCRIPTION,
  DECORATION_SET_ORDERED_DESCRIPTION,
  TILE_SET_DYNAMIC_BADGE,
  TILE_SET_DYNAMIC_DESCRIPTION,
  TILE_SET_ORDERED_BADGE,
  TILE_SET_ORDERED_DESCRIPTION,
} from "@/lib/tileSetUiLabels";
import type { TileCatalogPayload, TileManifestSet } from "@west-marches/shared";
import {
  getDecorationPaintSetById,
  getPaintSetIdForDecoration,
  isDynamicDecorationPaintSet,
  isDynamicTileSet,
  isOrderedDecorationPaintSet,
  isOrderedTileSet,
  resolveTileAssetUrl,
} from "@west-marches/shared";
import { Keyboard } from "lucide-react";
import { useMemo } from "react";

import { CatalogKindBadge, FolderGlyph } from "./CatalogKindBadge";
import { DynamicBrushBadge } from "./DynamicBrushBadge";
import {
  buildRootHotkeySlots,
  collectTaxonomySubtreeDecorationIds,
  collectTaxonomySubtreeTileSetIds,
  getRootDetailEntries,
  getTaxonomyDecorationEntries,
  getTaxonomyDetailEntries,
  getTileCatalogBrowserSectionGroups,
  type TileCatalogBrowserSection,
} from "./tileCatalogBrowserSections";
import { createTileCatalogSectionThumbnailResolver } from "./tileCatalogBrowserThumbnails";

export type { TileCatalogBrowserSection };

const CARD_HEADER_BADGES = "absolute left-2 top-2 z-10 flex items-center gap-2";
const CARD_HEADER_HOTKEY_BADGE =
  "inline-flex items-center justify-center gap-1.5 rounded border border-neutral-950/12 bg-[#fffaf1]/94 px-1.5 py-0.5 text-sm font-medium tabular-nums text-neutral-950/65 shadow-sm";
const TILE_SET_CARD_SIZE = "h-[340px] w-[340px]";
const DECORATION_CARD_SIZE = "h-[160px] w-[160px]";
const DECORATION_FOLDER_CARD_SIZE = "h-[180px] w-[180px]";
const DECORATION_IMAGE_CLASS = "absolute inset-0 h-full w-full object-contain p-4";
const DECORATION_FOLDER_IMAGE_CLASS = "absolute inset-0 h-full w-full object-contain p-3";

function HotkeyBadge({ hotkeyNum }: { hotkeyNum: number }) {
  return (
    <span className={CARD_HEADER_HOTKEY_BADGE} aria-hidden data-testid="tile-picker-hotkey-badge">
      <Keyboard className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
      <span>{hotkeyNum}</span>
    </span>
  );
}

interface FolderSectionCardProps {
  label: string;
  thumbnailUrl: string | null;
  hotkeyNum: number | null;
  kind: "folder" | "collection";
  /**
   * Visual variant. `"overlay"` shrinks the card and contains the thumbnail
   * (decorations are typically transparent PNGs). Defaults to `"tile"`.
   */
  variant?: "tile" | "overlay";
  onClick: () => void;
}

function FolderSectionCard({
  label,
  thumbnailUrl,
  hotkeyNum,
  kind,
  variant = "tile",
  onClick,
}: FolderSectionCardProps) {
  const theme = getMapChromeTheme();
  const ariaKeyShortcuts = hotkeyNum != null ? String(hotkeyNum) : undefined;
  const isOverlay = variant === "overlay";
  const cardSize = isOverlay ? DECORATION_FOLDER_CARD_SIZE : TILE_SET_CARD_SIZE;
  const imageClass = isOverlay
    ? DECORATION_FOLDER_IMAGE_CLASS
    : "absolute inset-0 h-full w-full object-cover";
  const footerClass = isOverlay
    ? "flex items-center justify-between border-t border-neutral-950/12 bg-[#fffaf1]/94 px-2.5 py-1.5"
    : "flex items-center justify-between border-t border-neutral-950/12 bg-[#fffaf1]/94 px-4 py-3.5";
  const labelClass = isOverlay
    ? "text-xs font-semibold text-neutral-950 truncate"
    : "text-lg font-semibold text-neutral-950";
  const metaClass = isOverlay
    ? "inline-flex items-center gap-1 text-[10px] font-medium text-neutral-950/65"
    : "inline-flex items-center gap-2 text-lg font-medium text-neutral-950/65";
  const folderGlyphClass = isOverlay ? "h-3 w-3 shrink-0" : "h-5 w-5 shrink-0";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex flex-col ${cardSize} shrink-0 overflow-hidden rounded-2xl transition-all hover:scale-[1.02] ${theme.cardIdleRing}`}
      aria-keyshortcuts={ariaKeyShortcuts}
    >
      {hotkeyNum != null ? (
        <span className={CARD_HEADER_BADGES}>
          <HotkeyBadge hotkeyNum={hotkeyNum} />
        </span>
      ) : null}
      <span className="relative min-h-0 flex-1 overflow-hidden">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={label}
            className={imageClass}
            loading="lazy"
          />
        ) : (
          <span className={`absolute inset-0 ${theme.cardFallback}`} />
        )}
        <span className={`absolute inset-0 ${theme.cardScrim}`} />
      </span>
      <span className={footerClass}>
        <span className={labelClass}>{label}</span>
        <span className={metaClass}>
          {kind === "folder" ? <FolderGlyph className={folderGlyphClass} /> : null}
          <span>{kind === "folder" ? "Folder" : "Collection"}</span>
        </span>
      </span>
    </button>
  );
}

interface TileCatalogBrowserProps {
  payload: TileCatalogPayload;
  /** Breadcrumb of sections; empty = top-level grid. Last entry is the active folder. */
  sectionPath: TileCatalogBrowserSection[];
  onSectionPathChange: (path: TileCatalogBrowserSection[]) => void;
  activeContentTab?: "tiles" | "decorations";
  onSelectTileSet?: (tileSetId: string) => void;
  /**
   * Invoked when the user picks a decoration thumbnail. The browser resolves
   * the clicked overlay to its paint set (singleton or shared) so the editor
   * store can drive decoration selection through the same paint-set model the
   * tile brush uses (`selectDecorationPaintSet`). Callers receive both the
   * paint set id and the originating overlay id; the latter is useful when
   * the paint set is a singleton and the caller wants to seed the brush
   * entry directly.
   */
  onSelectDecoration?: (input: {
    paintSetId: string;
    decorationId: string;
    taxonomyNodeId: string | null;
  }) => void;
  selectedTileSetId?: string | null;
  selectedDecorationId?: string | null;
  hiddenTileSetIds?: string[];
  heading?: string | null;
  detailAction?: React.ReactNode;
  renderTileSetAction?: (tileSet: TileManifestSet) => React.ReactNode;
}

export default function TileCatalogBrowser({
  payload,
  sectionPath,
  onSectionPathChange,
  activeContentTab = "tiles",
  onSelectTileSet,
  onSelectDecoration,
  selectedTileSetId,
  selectedDecorationId,
  hiddenTileSetIds = [],
  heading = "Tile Catalog",
  detailAction,
  renderTileSetAction,
}: TileCatalogBrowserProps) {
  const theme = getMapChromeTheme();
  const hiddenTileSetIdSet = useMemo(() => new Set(hiddenTileSetIds), [hiddenTileSetIds]);
  const atRoot = sectionPath.length === 0;
  const activeSection = atRoot ? null : sectionPath[sectionPath.length - 1]!;
  const taxonomyKindFilter: "tile" | "overlay" =
    activeContentTab === "decorations" ? "overlay" : "tile";
  const sectionGroups = useMemo(
    () => getTileCatalogBrowserSectionGroups(payload, taxonomyKindFilter),
    [payload, taxonomyKindFilter],
  );
  const rootDetailEntries = useMemo(
    () => getRootDetailEntries(payload, sectionGroups, hiddenTileSetIdSet, taxonomyKindFilter),
    [payload, sectionGroups, hiddenTileSetIdSet, taxonomyKindFilter],
  );
  const tileSetById = useMemo(
    () => new Map(payload.manifest.tileSets.map((tileSet) => [tileSet.id, tileSet])),
    [payload],
  );
  const decorationPaintSetById = useMemo(
    () =>
      new Map(
        (payload.browse.decorationPaintSets ?? []).map((paintSet) => [paintSet.id, paintSet] as const),
      ),
    [payload],
  );
  const getSectionThumbnail = useMemo(
    () => createTileCatalogSectionThumbnailResolver(payload),
    [payload],
  );
  const decorationUrlById = useMemo(
    () =>
      new Map(
        payload.browse.decorations.map((decoration) => [
          decoration.id,
          resolveTileAssetUrl(decoration.relativePath),
        ]),
      ),
    [payload],
  );

  const rootHotkeySlots = useMemo(
    () => buildRootHotkeySlots(payload, sectionGroups, hiddenTileSetIdSet, 9, taxonomyKindFilter),
    [payload, sectionGroups, hiddenTileSetIdSet, taxonomyKindFilter],
  );

  const sectionHotkeyByKey = useMemo(() => {
    const m = new Map<string, number>();
    rootHotkeySlots.forEach((slot, i) => {
      if (slot.kind === "section") {
        m.set(`${slot.section.kind}:${slot.section.id}`, i + 1);
      }
    });
    return m;
  }, [rootHotkeySlots]);
  const rootTileSetHotkeyById = useMemo(() => {
    const m = new Map<string, number>();
    rootHotkeySlots.forEach((slot, i) => {
      if (slot.kind === "tile-set") {
        m.set(slot.tileSetId, i + 1);
      }
    });
    return m;
  }, [rootHotkeySlots]);

  const detailEntries = useMemo(() => {
    if (!activeSection) {
      return [];
    }
    if (activeSection.kind === "taxonomy") {
      return getTaxonomyDetailEntries(
        payload,
        activeSection.id,
        hiddenTileSetIdSet,
        taxonomyKindFilter,
      );
    }
    return activeSection.tileSetIds
      .filter((tileSetId) => !hiddenTileSetIdSet.has(tileSetId))
      .map((tileSetId, index) => ({
        kind: "tile-set" as const,
        tileSetId,
        sortOrder: index,
      }));
  }, [activeSection, payload, hiddenTileSetIdSet]);
  const activeDecorations = useMemo(() => {
    if (!activeSection || activeSection.kind !== "taxonomy") {
      return [];
    }
    return getTaxonomyDecorationEntries(payload, activeSection.id);
  }, [activeSection, payload]);

  return (
    <div>
      {activeSection ? (
        <>
          {activeContentTab === "decorations" ? (
            <div className="flex flex-wrap gap-3">
              {activeDecorations.map((decoration) => {
                const decorationUrl = decorationUrlById.get(decoration.id) ?? "";
                const isSelected = selectedDecorationId === decoration.id;
                const paintSetId =
                  decoration.paintSetId ?? getPaintSetIdForDecoration(decoration.id);
                const paintSet = paintSetId
                  ? (decorationPaintSetById.get(paintSetId) ?? getDecorationPaintSetById(paintSetId))
                  : undefined;
                const dynamicLook = paintSet ? isDynamicDecorationPaintSet(paintSet) : false;
                const orderedLook = paintSet ? isOrderedDecorationPaintSet(paintSet) : false;
                return (
                  <div
                    key={decoration.id}
                    className={`relative ${DECORATION_CARD_SIZE} shrink-0 overflow-hidden rounded-2xl transition-all ${
                      isSelected ? theme.cardSelectedRing : theme.cardIdleRing
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (!onSelectDecoration || !paintSetId) {
                          return;
                        }
                        const taxonomyNodeId =
                          activeSection?.kind === "taxonomy" ? activeSection.id : null;
                        onSelectDecoration({
                          paintSetId,
                          decorationId: decoration.id,
                          taxonomyNodeId,
                        });
                      }}
                      className="absolute inset-0"
                      disabled={!onSelectDecoration || !paintSetId}
                    >
                      {decorationUrl ? (
                        <img
                          src={decorationUrl}
                          alt={decoration.title}
                          className={DECORATION_IMAGE_CLASS}
                          loading="lazy"
                        />
                      ) : (
                        <span className={`absolute inset-0 ${theme.cardFallback}`} />
                      )}
                      <span className={`absolute inset-0 ${theme.cardScrim}`} />
                    </button>
                    {dynamicLook || orderedLook ? (
                      <DynamicBrushBadge
                        className="absolute right-2 top-2 text-[10px]"
                        onActivate={() => {
                          if (paintSetId) {
                            onSelectDecoration?.({
                              paintSetId,
                              decorationId: decoration.id,
                              taxonomyNodeId:
                                activeSection?.kind === "taxonomy" ? activeSection.id : null,
                            });
                          }
                        }}
                        label={dynamicLook ? TILE_SET_DYNAMIC_BADGE : TILE_SET_ORDERED_BADGE}
                        description={
                          dynamicLook
                            ? DECORATION_SET_DYNAMIC_DESCRIPTION
                            : DECORATION_SET_ORDERED_DESCRIPTION
                        }
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              {detailEntries.map((entry, index) => {
                const hotkeyNum = index < 9 ? index + 1 : null;

                if (entry.kind === "section") {
                  const isOverlaySection = entry.section.nodeKind === "overlay";
                  const thumbnailUrl = getSectionThumbnail({
                    ...entry.section,
                    tileSetIds: isOverlaySection
                      ? entry.section.tileSetIds
                      : entry.countTileSetIds,
                    decorationIds: isOverlaySection
                      ? entry.countTileSetIds
                      : entry.section.decorationIds,
                  });

                  return (
                    <FolderSectionCard
                      key={`section:${entry.section.kind}:${entry.section.id}`}
                      label={entry.section.label}
                      thumbnailUrl={thumbnailUrl}
                      hotkeyNum={hotkeyNum}
                      kind="folder"
                      variant={isOverlaySection ? "overlay" : "tile"}
                      onClick={() =>
                        onSectionPathChange([
                          ...sectionPath,
                          { ...entry.section, tileSetIds: entry.visibleTileSetIds },
                        ])
                      }
                    />
                  );
                }

                if (entry.kind !== "tile-set") {
                  return null;
                }

                const tileSet = tileSetById.get(entry.tileSetId);
                if (!tileSet) {
                  return null;
                }
                const thumbnailUrl = getSectionThumbnail({
                  kind: "collection",
                  id: tileSet.id,
                  label: tileSet.label,
                  sortOrder: entry.sortOrder,
                  tileSetIds: [tileSet.id],
                });
                const isSelected = selectedTileSetId === tileSet.id;
                const dynamicLook = isDynamicTileSet(tileSet);
                const orderedLook = isOrderedTileSet(tileSet);

                return (
                  <div
                    key={tileSet.id}
                    className={`relative ${TILE_SET_CARD_SIZE} shrink-0 overflow-hidden rounded-2xl transition-all ${
                      isSelected ? theme.cardSelectedRing : theme.cardIdleRing
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => onSelectTileSet?.(tileSet.id)}
                      className="absolute inset-0"
                      disabled={!onSelectTileSet}
                      aria-keyshortcuts={hotkeyNum != null ? String(hotkeyNum) : undefined}
                    >
                      <span className={CARD_HEADER_BADGES}>
                        {hotkeyNum != null ? <HotkeyBadge hotkeyNum={hotkeyNum} /> : null}
                        <CatalogKindBadge kind="tile-set" />
                      </span>
                      {thumbnailUrl ? (
                        <img
                          src={thumbnailUrl}
                          alt={tileSet.label}
                          className="absolute inset-0 h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <span className={`absolute inset-0 ${theme.cardFallback}`} />
                      )}
                      <span className={`absolute inset-0 ${theme.cardScrim}`} />
                      <span className={`absolute inset-x-0 bottom-0 ${theme.cardFooter}`}>
                        <span className={theme.cardFooterTitle}>{tileSet.label}</span>
                        <span className={theme.cardFooterMeta}>
                          {tileSet.tileIds.length} {tileSet.tileIds.length === 1 ? "tile" : "tiles"}
                        </span>
                      </span>
                    </button>
                    {dynamicLook || orderedLook ? (
                      <DynamicBrushBadge
                        className="absolute right-2 top-2 text-[10px]"
                        onActivate={() => onSelectTileSet?.(tileSet.id)}
                        label={dynamicLook ? TILE_SET_DYNAMIC_BADGE : TILE_SET_ORDERED_BADGE}
                        description={
                          dynamicLook ? TILE_SET_DYNAMIC_DESCRIPTION : TILE_SET_ORDERED_DESCRIPTION
                        }
                      />
                    ) : null}
                    {renderTileSetAction ? renderTileSetAction(tileSet) : null}
                  </div>
                );
              })}
              {detailAction}
            </div>
          )}
        </>
      ) : (
        <>
          {heading !== null ? (
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-lg font-semibold ${theme.textPrimary}`}>{heading}</h2>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-3">
            {rootDetailEntries.map((entry) => {
              if (entry.kind === "section") {
                const section = entry.section;
                const isTaxonomySection = section.kind === "taxonomy";
                const isOverlaySection = isTaxonomySection && section.nodeKind === "overlay";
                const thumbnailSourceTileSetIds = isTaxonomySection
                  ? collectTaxonomySubtreeTileSetIds(payload.browse.taxonomy, section.id)
                  : section.tileSetIds;
                const thumbnailSourceDecorationIds = isOverlaySection
                  ? collectTaxonomySubtreeDecorationIds(payload.browse.taxonomy, section.id)
                  : section.decorationIds;
                const thumbnailUrl = getSectionThumbnail({
                  ...section,
                  tileSetIds: thumbnailSourceTileSetIds,
                  decorationIds: thumbnailSourceDecorationIds,
                });
                const sectionKey = `${section.kind}:${section.id}`;
                const hotkeyNum = sectionHotkeyByKey.get(sectionKey) ?? null;
                return (
                  <FolderSectionCard
                    key={sectionKey}
                    label={section.label}
                    thumbnailUrl={thumbnailUrl}
                    hotkeyNum={hotkeyNum}
                    kind={section.kind === "taxonomy" ? "folder" : "collection"}
                    variant={isOverlaySection ? "overlay" : "tile"}
                    onClick={() =>
                      onSectionPathChange([
                        {
                          ...section,
                          tileSetIds: entry.visibleTileSetIds,
                        },
                      ])
                    }
                  />
                );
              }

              const tileSet = tileSetById.get(entry.tileSetId);
              if (!tileSet) {
                return null;
              }
              const thumbnailUrl = getSectionThumbnail({
                kind: "collection",
                id: tileSet.id,
                label: tileSet.label,
                sortOrder: entry.sortOrder,
                tileSetIds: [tileSet.id],
              });
              const isSelected = selectedTileSetId === tileSet.id;
              const dynamicLook = isDynamicTileSet(tileSet);
              const orderedLook = isOrderedTileSet(tileSet);
              const hotkeyNum = rootTileSetHotkeyById.get(tileSet.id) ?? null;

              return (
                <div
                  key={tileSet.id}
                  className={`relative ${TILE_SET_CARD_SIZE} shrink-0 overflow-hidden rounded-2xl transition-all ${
                    isSelected ? theme.cardSelectedRing : theme.cardIdleRing
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => onSelectTileSet?.(tileSet.id)}
                    className="absolute inset-0"
                    disabled={!onSelectTileSet}
                    aria-keyshortcuts={hotkeyNum != null ? String(hotkeyNum) : undefined}
                  >
                    <span className={CARD_HEADER_BADGES}>
                      {hotkeyNum != null ? <HotkeyBadge hotkeyNum={hotkeyNum} /> : null}
                      <CatalogKindBadge kind="tile-set" />
                    </span>
                    {thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt={tileSet.label}
                        className="absolute inset-0 h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <span className={`absolute inset-0 ${theme.cardFallback}`} />
                    )}
                    <span className={`absolute inset-0 ${theme.cardScrim}`} />
                    <span className={`absolute inset-x-0 bottom-0 ${theme.cardFooter}`}>
                      <span className={theme.cardFooterTitle}>{tileSet.label}</span>
                      <span className={theme.cardFooterMeta}>
                        {tileSet.tileIds.length} {tileSet.tileIds.length === 1 ? "tile" : "tiles"}
                      </span>
                    </span>
                  </button>
                  {dynamicLook || orderedLook ? (
                    <DynamicBrushBadge
                      className="absolute right-2 top-2 text-[10px]"
                      onActivate={() => onSelectTileSet?.(tileSet.id)}
                      label={dynamicLook ? TILE_SET_DYNAMIC_BADGE : TILE_SET_ORDERED_BADGE}
                      description={dynamicLook ? TILE_SET_DYNAMIC_DESCRIPTION : TILE_SET_ORDERED_DESCRIPTION}
                    />
                  ) : null}
                  {renderTileSetAction ? renderTileSetAction(tileSet) : null}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
